import { readMoneyV2, readRateV2, type LegacyRateUnrepresentable, type RateUnit, type RateV2 } from "./money-v2";
import type { ProjectionErrorCode } from "./money-v2-projections";

// Readers for the money contract 2 management projections of route families
// F6 (unit tariff), F7 (pricing-profile catalog) and F14 (unit pricing context),
// contracts/currency/management_projection.schema.json. Rates go through the
// value reader of money-v2.ts; this module adds what a management body
// guarantees about them: the tariff state and its reason, every rate in the
// tariff currency, a rate unit that matches the stored uom, rules in stored
// order, one server choice per currency, and the response-level rule that a
// body whose records are not eligible legacy USD carries no legacy money keys.
//
// Nothing here knows which currencies exist or may be used: the currency of
// new prices is the tenant's operating currency the server projects, and a
// write in it is allowed only by the server's verdicts, never by a client
// list, the browser language or a default. A body reads as "unavailable" when
// it is null or absent and as "invalid" when it breaks the contract, so a
// consumer has no path from a broken management body to its legacy pricebook.
//
// The operating currency is a display fact read on its own: a missing,
// malformed or contradictory member makes only that member unreadable (never
// USD), while the stored tariff money keeps its own exact validation and is
// never relabelled with the tenant's currency.
//
// Keys the contract does not define are ignored (forward compatibility); the
// closed schema objects are the server's rule when writing.

export type TariffState = "configured" | "unset" | "invalid" | "unavailable";
export type TariffInvalidReason = "pricebook_malformed" | "currency_missing" | "currency_not_in_registry" | "money_version_2_invalid";
export type TariffReason = "rate_unrepresentable" | TariffInvalidReason | "stored_tariff_unavailable";
export type NewWriteVerdict = "eligible" | "ineligible" | "activation_blocked";
export type NewWriteReason = "known_old_client_would_fail" | "unknown_consumer" | "financial_readiness";
/** A tenant verdict reason: a currency other than the operating one, or a tenant whose currency cannot be read. */
export type TenantWriteReason = "not_operating_currency" | "operating_currency_unavailable";
export type OperatingCurrencyAssignment = "assigned" | "unassigned" | "unavailable";

export const TARIFF_STATES: readonly TariffState[] = ["configured", "unset", "invalid", "unavailable"];
export const NEW_WRITE_VERDICTS: readonly NewWriteVerdict[] = ["eligible", "ineligible", "activation_blocked"];
export const OPERATING_CURRENCY_ASSIGNMENTS: readonly OperatingCurrencyAssignment[] = ["assigned", "unassigned", "unavailable"];

const INVALID_REASONS: readonly TariffInvalidReason[] = ["pricebook_malformed", "currency_missing", "currency_not_in_registry", "money_version_2_invalid"];
const NEW_WRITE_REASONS: readonly NewWriteReason[] = ["known_old_client_would_fail", "unknown_consumer", "financial_readiness"];
const TENANT_WRITE_REASONS: readonly TenantWriteReason[] = ["not_operating_currency", "operating_currency_unavailable"];
/** Stored pricebook uom -> contract rate unit (domain/billing/rate_projection.py). */
const RATE_UNIT_BY_UOM: Readonly<Record<string, RateUnit>> = { kwh: "kWh", hour: "hour", session: "session" };
/** Legacy money keys of the F6 bodies and of a catalog profile. */
const TARIFF_LEGACY_KEYS = ["pricebook", "tariff", "currency", "energy_rate", "hourly_rate", "idle_fee", "activation_fee"];
const PROFILE_LEGACY_KEYS = ["pricebook"];
/** Legacy money key of an F14 body; each applied profile carries PROFILE_LEGACY_KEYS. */
const CONTEXT_LEGACY_KEYS = ["default_pricebook"];
/** Tenant members of the F6/F7 money_v2, which an F14 body never carries. */
const TENANT_MEMBERS = ["operating_currency", "currency_choices"];
const RULE_TEXT_FIELDS = ["fee_type", "uom", "start_time", "end_time", "days"] as const;

/** Sent instead of the rate of a stored rule whose uom is not a contract rate unit; raw is the stored text. */
export interface RateUnitUnsupported {
  readonly diagnostic: "rate_unit_unsupported";
  readonly raw: string;
  readonly money_version: 2;
}

export type TariffRuleRate = RateV2 | LegacyRateUnrepresentable | RateUnitUnsupported;

/** One stored rule, in stored order (index is its position), with the window the engine reads. */
export interface TariffRuleV2 {
  readonly index: number;
  readonly fee_type: string;
  readonly uom: string;
  readonly is_tou: boolean;
  readonly start_time: string;
  readonly end_time: string;
  readonly days: string;
  readonly priority: number;
  readonly rate: TariffRuleRate;
}

export interface ConfiguredTariffV2 {
  readonly tariff_state: "configured";
  readonly currency: string;
  readonly exponent: number;
  readonly stored_money_version: 2 | null;
  readonly rules: readonly TariffRuleV2[];
  /** rate_unrepresentable when a stored rate has no finite non-negative decimal. */
  readonly reason: null | "rate_unrepresentable";
}

/** No stored tariff: no currency and no rules; never priced and never labelled USD. */
export interface UnsetTariffV2 {
  readonly tariff_state: "unset";
  readonly currency: null;
  readonly exponent: null;
  readonly stored_money_version: null;
  readonly rules: readonly [];
  readonly reason: null;
}

/** A stored tariff that is present but unreadable. */
export interface InvalidTariffV2 {
  readonly tariff_state: "invalid";
  readonly currency: null;
  readonly exponent: null;
  readonly stored_money_version: 2 | null;
  readonly rules: null;
  readonly reason: TariffInvalidReason;
}

/** The stored record could not be read at all. */
export interface UnavailableTariffV2 {
  readonly tariff_state: "unavailable";
  readonly currency: null;
  readonly exponent: null;
  readonly stored_money_version: null;
  readonly rules: null;
  readonly reason: "stored_tariff_unavailable";
}

export type TariffMoneyV2 = ConfiguredTariffV2 | UnsetTariffV2 | InvalidTariffV2 | UnavailableTariffV2;

/** A currency the editor may show; only an eligible verdict allows a new write. */
export interface CurrencyChoiceV2 {
  readonly currency: string;
  readonly exponent: number;
  /** A currency already stored in the response's records. */
  readonly stored: boolean;
  readonly new_write_verdict: NewWriteVerdict;
  /** A tenant reason comes only with an ineligible verdict. */
  readonly new_write_reason: NewWriteReason | TenantWriteReason | null;
}

/**
 * The operating currency of the tenant the answer belongs to, set by the platform
 * supervisor (assigned) or the legacy USD contour (unassigned). A display fact: the
 * write transactions stay the authority. Assignment metadata is not part of it.
 */
export type OperatingCurrencyV2 =
  | {
    readonly assignment: "assigned" | "unassigned";
    readonly currency: string;
    readonly new_write_verdict: NewWriteVerdict;
    readonly new_write_reason: NewWriteReason | null;
  }
  | {
    /** A stored value that is not a known currency: never USD. */
    readonly assignment: "unavailable";
    readonly currency: null;
    readonly new_write_verdict: "ineligible";
    readonly new_write_reason: "operating_currency_unavailable";
  };

/** money_v2 of an F6 GET or successful POST body. */
export interface TariffManagementV2 {
  readonly tariff: TariffMoneyV2;
  /** Read on its own: unavailable when absent, invalid when malformed or contradicted by the choices. */
  readonly operating_currency: ManagementRead<OperatingCurrencyV2>;
  readonly currency_choices: readonly CurrencyChoiceV2[];
}

/** One catalog profile of an F7 body: its identity and its exact tariff projection. */
export interface PricingProfileV2 {
  readonly id: number;
  readonly organization_id: number;
  readonly name: string;
  readonly pricebook_version: string;
  readonly revision: number;
  readonly enabled: boolean;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly tariff: TariffMoneyV2;
}

/** F7 GET/PUT item, create, disable and from-unit bodies. */
export interface PricingProfileManagementV2 {
  readonly profile: PricingProfileV2;
  readonly operating_currency: ManagementRead<OperatingCurrencyV2>;
  readonly currency_choices: readonly CurrencyChoiceV2[];
}

/** F7 list body, profiles in server order. */
export interface PricingProfileListManagementV2 {
  readonly profiles: readonly PricingProfileV2[];
  readonly operating_currency: ManagementRead<OperatingCurrencyV2>;
  readonly currency_choices: readonly CurrencyChoiceV2[];
}

/**
 * One applied profile of an F14 body: the assignment as the server's builder lists it and
 * the exact tariff of the profile row it selected (never unset: a catalog row always stores
 * a pricebook). scope and precedence_explanation are server text, shown verbatim.
 */
export interface AppliedPricingProfileV2 {
  readonly assignment_id: number;
  readonly profile_id: number;
  readonly profile_name: string;
  readonly audience_type: string;
  readonly audience_id: number;
  readonly audience_name: string;
  readonly resource_type: string;
  readonly scope: string;
  readonly scope_type: string;
  readonly status: string;
  readonly valid_from_utc: string | null;
  readonly valid_until_utc: string | null;
  readonly precedence: number;
  readonly money_v2: TariffMoneyV2;
}

/**
 * F14 body (GET /api/units/<id>/pricing-context?money_contract=2): the unit's persisted
 * tariff and every applied profile, most specific first. No tenant currency or choices:
 * the panel only displays. Legacy pricebooks are never part of the read.
 */
export interface PricingContextV2 {
  readonly unit_id: string;
  readonly organization_id: number;
  readonly organization_name: string | null;
  readonly applied_profiles: readonly AppliedPricingProfileV2[];
  readonly precedence_explanation: string;
  readonly money_v2: { readonly tariff: TariffMoneyV2 };
}

/**
 * A successful GET or PUT /api/org/<id>/operating-currency body: the tenant's stored
 * operating currency, the currency of its new writes and the supervisor's candidates.
 */
export interface OperatingCurrencyAssignmentV2 {
  readonly organization_id: number;
  /** The stored code; null when unassigned or unavailable (a malformed stored value is never echoed). */
  readonly operating_currency: string | null;
  /** The tenant's verdict, as in money_v2.operating_currency. */
  readonly operating: OperatingCurrencyV2;
  readonly assigned_at: string | null;
  readonly assigned_by: number | null;
  /**
   * Codes a platform supervisor may assign, sorted. Configuration candidates only:
   * never activation, a new-write permission, permission to change an assigned
   * currency or a waiver of the PUT preconditions.
   */
  readonly assignment_choices: readonly string[];
}

export type ManagementErrorCode =
  | ProjectionErrorCode
  | "money_contract"
  | "state"
  | "reason"
  | "rule_field"
  | "profile_field"
  | "rule_index"
  | "unit_mismatch"
  | "duplicate_choice"
  | "choice"
  | "choice_missing"
  | "choice_not_stored"
  | "legacy_keys_present"
  | "operating_currency"
  | "assignment_field"
  | "assignment_choices"
  | "organization_mismatch"
  | "context_field"
  | "tenant_members_present";

/** Like ProjectionRead; path locates the offending field ("money_v2.tariff.rules[1].rate"). */
export type ManagementRead<T> =
  | { readonly status: "value"; readonly value: T }
  | { readonly status: "unavailable" }
  | { readonly status: "invalid"; readonly error: ManagementErrorCode; readonly path: string; readonly detail: string };

class Invalid extends Error {
  readonly error: ManagementErrorCode;
  readonly path: string;
  readonly detail: string;

  constructor(error: ManagementErrorCode, path: string, detail: string) {
    super(`${path}: ${error}: ${detail}`);
    this.error = error;
    this.path = path;
    this.detail = detail;
  }
}

const show = (value: unknown) => (typeof value === "string" ? JSON.stringify(value) : String(value));
const join = (path: string, key: string) => (path ? `${path}.${key}` : key);
const has = (record: Record<string, unknown>, key: string) => Object.prototype.hasOwnProperty.call(record, key);

function object(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Invalid("not_object", path, show(value));
  return value as Record<string, unknown>;
}

function array(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) throw new Invalid("not_array", path, show(value));
  return value;
}

function field(record: Record<string, unknown>, key: string, path: string): unknown {
  if (!has(record, key)) throw new Invalid("missing_field", join(path, key), key);
  return record[key];
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], path: string): T {
  if (!allowed.includes(value as T)) throw new Invalid("enum", path, show(value));
  return value as T;
}

function integer(value: unknown, path: string, error: ManagementErrorCode = "rule_field"): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) throw new Invalid(error, path, show(value));
  return value;
}

function text(value: unknown, path: string, error: ManagementErrorCode = "rule_field"): string {
  if (typeof value !== "string") throw new Invalid(error, path, show(value));
  return value;
}

function boolean(value: unknown, path: string, error: ManagementErrorCode = "rule_field"): boolean {
  if (typeof value !== "boolean") throw new Invalid(error, path, show(value));
  return value;
}

function nullField(record: Record<string, unknown>, key: string, path: string): null {
  const value = field(record, key, path);
  if (value !== null) throw new Invalid("state", join(path, key), `${show(value)} in a ${show(record.tariff_state)} tariff`);
  return null;
}

// A currency and exponent are valid exactly when a zero amount in them is, so
// the value reader's own rules apply without a second copy.
function currencyHeader(currency: unknown, exponent: unknown, path: string): { currency: string; exponent: number } {
  const probe = readMoneyV2({ currency, amount_minor: "0", exponent, money_version: 2 });
  if (probe.status === "value") return { currency: probe.value.currency, exponent: probe.value.exponent };
  const error = probe.status === "invalid" ? probe.error : "currency";
  throw new Invalid(error, join(path, error === "exponent" ? "exponent" : "currency"), show(error === "exponent" ? exponent : currency));
}

function storedMoneyVersion(record: Record<string, unknown>, path: string): 2 | null {
  const value = field(record, "stored_money_version", path);
  if (value !== null && value !== 2) throw new Invalid("money_version", join(path, "stored_money_version"), show(value));
  return value;
}

function ruleRate(value: unknown, uom: string, currency: string, path: string): TariffRuleRate {
  const unit = Object.prototype.hasOwnProperty.call(RATE_UNIT_BY_UOM, uom) ? RATE_UNIT_BY_UOM[uom] : undefined;
  if (typeof value === "object" && value !== null && (value as Record<string, unknown>).diagnostic === "rate_unit_unsupported") {
    const record = value as Record<string, unknown>;
    if (field(record, "money_version", path) !== 2) throw new Invalid("money_version", join(path, "money_version"), show(record.money_version));
    const raw = field(record, "raw", path);
    if (typeof raw !== "string" || raw === "") throw new Invalid("raw", join(path, "raw"), show(raw));
    if (unit !== undefined) throw new Invalid("unit_mismatch", path, `uom ${show(uom)} is a contract rate unit`);
    return { diagnostic: "rate_unit_unsupported", raw, money_version: 2 };
  }
  const read = readRateV2(value);
  if (read.status === "unavailable") throw new Invalid("not_object", path, show(value));
  if (read.status === "invalid") throw new Invalid(read.error, path, read.detail);
  const rate = read.value;
  if (unit === undefined || rate.unit !== unit) throw new Invalid("unit_mismatch", join(path, "unit"), `${rate.unit} for uom ${show(uom)}`);
  if (!("diagnostic" in rate) && rate.currency !== currency) throw new Invalid("currency_mismatch", join(path, "currency"), `${rate.currency} in a ${currency} tariff`);
  return rate;
}

function rule(value: unknown, position: number, currency: string, path: string): TariffRuleV2 {
  const record = object(value, path);
  const index = integer(field(record, "index", path), join(path, "index"), "rule_index");
  if (index !== position) throw new Invalid("rule_index", join(path, "index"), `index ${index} at position ${position}`);
  const fields = Object.fromEntries(RULE_TEXT_FIELDS.map((key) => [key, text(field(record, key, path), join(path, key))])) as Record<(typeof RULE_TEXT_FIELDS)[number], string>;
  const is_tou = boolean(field(record, "is_tou", path), join(path, "is_tou"));
  const priority = integer(field(record, "priority", path), join(path, "priority"));
  const rate = ruleRate(field(record, "rate", path), fields.uom, currency, join(path, "rate"));
  return { index, ...fields, is_tou, priority, rate };
}

function tariff(value: unknown, path: string): TariffMoneyV2 {
  const record = object(value, path);
  const state = oneOf(field(record, "tariff_state", path), TARIFF_STATES, join(path, "tariff_state"));
  const reasonPath = join(path, "reason");
  const reason = field(record, "reason", path);
  for (const key of ["currency", "exponent", "rules"]) field(record, key, path);

  if (state === "configured") {
    const head = currencyHeader(record.currency, record.exponent, path);
    const stored_money_version = storedMoneyVersion(record, path);
    const rules = array(record.rules, join(path, "rules")).map((r, i) => rule(r, i, head.currency, `${join(path, "rules")}[${i}]`));
    if (reason !== null && reason !== "rate_unrepresentable") throw new Invalid("reason", reasonPath, show(reason));
    // A rate without a finite decimal makes the tariff unrepresentable. A uom outside the
    // contract carries its raw text instead and may hide one, but some rule must explain it.
    if (reason === null && rules.some((r) => "diagnostic" in r.rate && r.rate.diagnostic === "legacy_rate_unrepresentable"))
      throw new Invalid("reason", reasonPath, "an unrepresentable rate in a tariff without reason rate_unrepresentable");
    if (reason === "rate_unrepresentable" && !rules.some((r) => "diagnostic" in r.rate))
      throw new Invalid("reason", reasonPath, "rate_unrepresentable without a diagnostic rate");
    return { tariff_state: "configured", currency: head.currency, exponent: head.exponent, stored_money_version, rules, reason };
  }

  nullField(record, "currency", path);
  nullField(record, "exponent", path);
  if (state === "unset") {
    nullField(record, "stored_money_version", path);
    const rules = array(record.rules, join(path, "rules"));
    if (rules.length !== 0) throw new Invalid("state", join(path, "rules"), `${rules.length} rules in an unset tariff`);
    if (reason !== null) throw new Invalid("reason", reasonPath, show(reason));
    return { tariff_state: "unset", currency: null, exponent: null, stored_money_version: null, rules: [], reason: null };
  }
  nullField(record, "rules", path);
  if (state === "invalid") {
    const stored_money_version = storedMoneyVersion(record, path);
    return { tariff_state: "invalid", currency: null, exponent: null, stored_money_version, rules: null, reason: oneOf(reason, INVALID_REASONS, reasonPath) };
  }
  nullField(record, "stored_money_version", path);
  if (reason !== "stored_tariff_unavailable") throw new Invalid("reason", reasonPath, show(reason));
  return { tariff_state: "unavailable", currency: null, exponent: null, stored_money_version: null, rules: null, reason };
}

function choice(value: unknown, path: string): CurrencyChoiceV2 {
  const record = object(value, path);
  const head = currencyHeader(field(record, "currency", path), field(record, "exponent", path), path);
  const stored = boolean(field(record, "stored", path), join(path, "stored"), "choice");
  const new_write_verdict = oneOf(field(record, "new_write_verdict", path), NEW_WRITE_VERDICTS, join(path, "new_write_verdict"));
  const rawReason = field(record, "new_write_reason", path);
  const reasonPath = join(path, "new_write_reason");
  if (new_write_verdict === "eligible") {
    if (rawReason !== null) throw new Invalid("choice", reasonPath, `${show(rawReason)} with an eligible verdict`);
    return { ...head, stored, new_write_verdict, new_write_reason: null };
  }
  // Only a currency already stored may be offered without being eligible, and then only for display.
  if (!stored) throw new Invalid("choice", join(path, "stored"), `${new_write_verdict} currency that is not stored`);
  const new_write_reason = oneOf(rawReason, [...NEW_WRITE_REASONS, ...TENANT_WRITE_REASONS], reasonPath);
  if ((TENANT_WRITE_REASONS as readonly string[]).includes(new_write_reason) && new_write_verdict !== "ineligible")
    throw new Invalid("choice", reasonPath, `${show(new_write_reason)} with a ${new_write_verdict} verdict`);
  return { ...head, stored, new_write_verdict, new_write_reason };
}

// A currency code alone is valid exactly when a zero amount in it is, at any exponent.
function currencyCode(value: unknown, path: string): string {
  return currencyHeader(value, 0, path).currency;
}

function operatingCurrency(value: unknown, path: string): OperatingCurrencyV2 {
  const record = object(value, path);
  const assignment = oneOf(field(record, "assignment", path), OPERATING_CURRENCY_ASSIGNMENTS, join(path, "assignment"));
  const rawCurrency = field(record, "currency", path);
  const new_write_verdict = oneOf(field(record, "new_write_verdict", path), NEW_WRITE_VERDICTS, join(path, "new_write_verdict"));
  const rawReason = field(record, "new_write_reason", path);
  const reasonPath = join(path, "new_write_reason");
  if (assignment === "unavailable") {
    if (rawCurrency !== null) throw new Invalid("operating_currency", join(path, "currency"), `${show(rawCurrency)} for an unavailable operating currency`);
    if (new_write_verdict !== "ineligible" || rawReason !== "operating_currency_unavailable")
      throw new Invalid("operating_currency", reasonPath, `${new_write_verdict} ${show(rawReason)} for an unavailable operating currency`);
    return { assignment, currency: null, new_write_verdict, new_write_reason: rawReason };
  }
  const currency = currencyCode(rawCurrency, path);
  // Unassigned is the legacy contour, which is USD and nothing else.
  if (assignment === "unassigned" && currency !== "USD") throw new Invalid("operating_currency", join(path, "currency"), `${currency} for an unassigned tenant`);
  if (new_write_verdict === "eligible") {
    if (rawReason !== null) throw new Invalid("operating_currency", reasonPath, `${show(rawReason)} with an eligible verdict`);
    return { assignment, currency, new_write_verdict, new_write_reason: null };
  }
  return { assignment, currency, new_write_verdict, new_write_reason: oneOf(rawReason, NEW_WRITE_REASONS, reasonPath) };
}

// The choices are scoped to the tenant: its own currency carries the tenant's verdict and is
// the only one that may be eligible, any other is not_operating_currency, and an
// unavailable tenant offers nothing writable.
function agreeTenant(tenant: OperatingCurrencyV2, list: readonly CurrencyChoiceV2[], path: string): void {
  list.forEach((c, i) => {
    const at = `${path}[${i}]`;
    if (tenant.assignment === "unavailable") {
      if (c.new_write_reason !== "operating_currency_unavailable") throw new Invalid("operating_currency", at, `${c.currency} ${c.new_write_verdict} for an unavailable operating currency`);
    } else if (c.currency === tenant.currency) {
      if (c.new_write_verdict !== tenant.new_write_verdict || c.new_write_reason !== tenant.new_write_reason)
        throw new Invalid("operating_currency", at, `${c.currency} ${c.new_write_verdict} for an operating currency ${tenant.new_write_verdict}`);
    } else if (c.new_write_reason !== "not_operating_currency") {
      throw new Invalid("operating_currency", at, `${c.currency} ${c.new_write_verdict} for operating currency ${tenant.currency}`);
    }
  });
}

/** The money_v2 operating_currency member of a body, checked against the body's choices. */
function tenantMember(moneyV2: Record<string, unknown>, list: readonly CurrencyChoiceV2[]): ManagementRead<OperatingCurrencyV2> {
  return block(moneyV2.operating_currency, (value) => {
    const tenant = operatingCurrency(value, "money_v2.operating_currency");
    agreeTenant(tenant, list, "money_v2.currency_choices");
    return tenant;
  });
}

function choices(value: unknown, path: string): readonly CurrencyChoiceV2[] {
  const list = array(value, path).map((c, i) => choice(c, `${path}[${i}]`));
  const seen = new Set<string>();
  list.forEach((c, i) => {
    if (seen.has(c.currency)) throw new Invalid("duplicate_choice", `${path}[${i}]`, c.currency);
    seen.add(c.currency);
  });
  return list;
}

const eligibleLegacy = (t: TariffMoneyV2) => t.tariff_state === "unset" || (t.tariff_state === "configured" && t.reason === null && t.currency === "USD");

// Every stored currency of the records is a choice marked stored, with its exponent,
// and a choice marked stored belongs to one of the records.
function agreeChoices(tariffs: readonly TariffMoneyV2[], list: readonly CurrencyChoiceV2[], path: string): void {
  const storedCurrencies = new Map<string, number>();
  for (const t of tariffs) if (t.tariff_state === "configured") storedCurrencies.set(t.currency, t.exponent);
  for (const [currency, exponent] of storedCurrencies) {
    const found = list.find((c) => c.currency === currency);
    if (found === undefined || !found.stored) throw new Invalid("choice_missing", path, `stored ${currency} is not a stored choice`);
    if (found.exponent !== exponent) throw new Invalid("exponent_mismatch", path, `${currency} choice exponent ${found.exponent}, tariff exponent ${exponent}`);
  }
  list.forEach((c, i) => {
    if (c.stored && !storedCurrencies.has(c.currency)) throw new Invalid("choice_not_stored", `${path}[${i}]`, `${c.currency} is not stored in this response`);
  });
}

function noLegacyKeys(record: Record<string, unknown>, keys: readonly string[], path: string): void {
  const present = keys.find((key) => has(record, key));
  if (present !== undefined) throw new Invalid("legacy_keys_present", join(path, present), "a record outside the legacy USD contour carries a legacy money key");
}

function body<T>(input: unknown, parse: (record: Record<string, unknown>) => T): ManagementRead<T> {
  if (input === null || input === undefined) return { status: "unavailable" };
  try {
    const record = object(input, "");
    if (record.money_contract !== 2 || !has(record, "money_contract")) throw new Invalid("money_contract", "money_contract", show(record.money_contract));
    return { status: "value", value: deepFreeze(parse(record)) };
  } catch (e) {
    if (e instanceof Invalid) return { status: "invalid", error: e.error, path: e.path, detail: e.detail };
    throw e;
  }
}

function block<T>(input: unknown, parse: (value: unknown) => T): ManagementRead<T> {
  if (input === null || input === undefined) return { status: "unavailable" };
  try {
    return { status: "value", value: deepFreeze(parse(input)) };
  } catch (e) {
    if (e instanceof Invalid) return { status: "invalid", error: e.error, path: e.path, detail: e.detail };
    throw e;
  }
}

function deepFreeze<T>(input: T): T {
  if (typeof input === "object" && input !== null && !Object.isFrozen(input)) {
    for (const member of Object.values(input)) deepFreeze(member);
    Object.freeze(input);
  }
  return input;
}

function profile(value: unknown, path: string): PricingProfileV2 {
  const record = object(value, path);
  const optionalText = (key: string) => {
    const v = field(record, key, path);
    return v === null ? null : text(v, join(path, key), "profile_field");
  };
  return {
    id: integer(field(record, "id", path), join(path, "id"), "profile_field"),
    organization_id: integer(field(record, "organization_id", path), join(path, "organization_id"), "profile_field"),
    name: text(field(record, "name", path), join(path, "name"), "profile_field"),
    pricebook_version: text(field(record, "pricebook_version", path), join(path, "pricebook_version"), "profile_field"),
    revision: integer(field(record, "revision", path), join(path, "revision"), "profile_field"),
    enabled: boolean(field(record, "enabled", path), join(path, "enabled"), "profile_field"),
    created_at: optionalText("created_at"),
    updated_at: optionalText("updated_at"),
    tariff: tariff(field(record, "money_v2", path), join(path, "money_v2")),
  };
}

function profiles(record: Record<string, unknown>, raw: readonly unknown[], pathOf: (i: number) => string): PricingProfileListManagementV2 {
  const list = raw.map((p, i) => profile(p, pathOf(i)));
  // Legacy keys are response-level: one ineligible profile makes the legacy answer a whole 409.
  if (!list.every((p) => eligibleLegacy(p.tariff))) raw.forEach((p, i) => noLegacyKeys(p as Record<string, unknown>, PROFILE_LEGACY_KEYS, pathOf(i)));
  const moneyV2 = object(field(record, "money_v2", ""), "money_v2");
  const currency_choices = choices(field(moneyV2, "currency_choices", "money_v2"), "money_v2.currency_choices");
  agreeChoices(list.map((p) => p.tariff), currency_choices, "money_v2.currency_choices");
  return { profiles: list, operating_currency: tenantMember(moneyV2, currency_choices), currency_choices };
}

/** Reads one `tariff_money_v2` block (an F6 tariff or one profile's money_v2). */
export function readTariffMoneyV2(input: unknown): ManagementRead<TariffMoneyV2> {
  return block(input, (value) => tariff(value, ""));
}

/** Reads one `operating_currency` member on its own (the body readers also check it against their choices). */
export function readOperatingCurrencyV2(input: unknown): ManagementRead<OperatingCurrencyV2> {
  return block(input, (value) => operatingCurrency(value, ""));
}

/** Reads a `currency_choices` list: valid codes and exponents, one choice per currency, verdict and reason consistent. */
export function readCurrencyChoicesV2(input: unknown): ManagementRead<readonly CurrencyChoiceV2[]> {
  return block(input, (value) => choices(value, ""));
}

// Sorted and distinct, as the server lists them; a code's registry membership is the server's rule.
function assignmentChoices(value: unknown, path: string): readonly string[] {
  const codes: string[] = [];
  array(value, path).forEach((raw, i) => {
    const at = `${path}[${i}]`;
    if (typeof raw !== "string") throw new Invalid("assignment_choices", at, show(raw));
    const code = currencyCode(raw, at);
    const previous = codes[codes.length - 1];
    if (previous !== undefined && code <= previous) throw new Invalid("assignment_choices", at, `${code} after ${previous}`);
    codes.push(code);
  });
  return codes;
}

function assignmentResponse(value: unknown, organizationId: number): OperatingCurrencyAssignmentV2 {
  const record = object(value, "");
  const organization_id = integer(field(record, "organization_id", ""), "organization_id", "assignment_field");
  // An answer about another organization is never shown or confirmed for this one.
  if (organization_id !== organizationId) throw new Invalid("organization_mismatch", "organization_id", `${organization_id} for organization ${organizationId}`);
  const rawStored = field(record, "operating_currency", "");
  let operating: OperatingCurrencyV2;
  try {
    operating = operatingCurrency({
      assignment: field(record, "assignment", ""),
      currency: field(record, "effective_currency", ""),
      new_write_verdict: field(record, "new_write_verdict", ""),
      new_write_reason: field(record, "new_write_reason", ""),
    }, "");
  } catch (e) {
    if (e instanceof Invalid && e.path === "currency") throw new Invalid(e.error, "effective_currency", e.detail);
    throw e;
  }
  const operating_currency = rawStored === null ? null : currencyCode(rawStored, "operating_currency");
  // The stored code is the effective one exactly when assigned; otherwise nothing is stored.
  if (operating.assignment === "assigned" ? operating_currency !== operating.currency : operating_currency !== null)
    throw new Invalid("operating_currency", "operating_currency", `${show(rawStored)} for ${operating.assignment} ${show(operating.currency)}`);
  const assigned_at = field(record, "assigned_at", "");
  if (assigned_at !== null && (typeof assigned_at !== "string" || assigned_at === "")) throw new Invalid("assignment_field", "assigned_at", show(assigned_at));
  const assigned_by = field(record, "assigned_by", "");
  if (assigned_by !== null) integer(assigned_by, "assigned_by", "assignment_field");
  const assignment_choices = assignmentChoices(field(record, "assignment_choices", ""), "assignment_choices");
  return { organization_id, operating_currency, operating, assigned_at, assigned_by: assigned_by as number | null, assignment_choices };
}

/**
 * Reads a successful GET or PUT /api/org/<id>/operating-currency body for the
 * organization it was requested for: required members, the assignment and
 * verdict rules of money_v2.operating_currency, the stored code agreeing with
 * the assignment, and sorted distinct candidates. An answer naming another
 * organization is invalid; nothing is ever read as USD.
 */
export function readOperatingCurrencyAssignmentV2(input: unknown, organizationId: number): ManagementRead<OperatingCurrencyAssignmentV2> {
  return block(input, (value) => assignmentResponse(value, organizationId));
}

/**
 * Reads an F6 body (GET /api/unit/<id>/tariff?money_contract=2, or a successful
 * POST): the marker, the persisted tariff, its server choices, the tenant's
 * operating currency and the legacy-key rule. A body without the marker is
 * invalid, never legacy USD.
 */
export function readTariffResponseV2(input: unknown): ManagementRead<TariffManagementV2> {
  return body(input, (record) => {
    const moneyV2 = object(field(record, "money_v2", ""), "money_v2");
    const stored = tariff(field(moneyV2, "tariff", "money_v2"), "money_v2.tariff");
    const currency_choices = choices(field(moneyV2, "currency_choices", "money_v2"), "money_v2.currency_choices");
    agreeChoices([stored], currency_choices, "money_v2.currency_choices");
    if (!eligibleLegacy(stored)) noLegacyKeys(record, TARIFF_LEGACY_KEYS, "");
    return { tariff: stored, operating_currency: tenantMember(moneyV2, currency_choices), currency_choices };
  });
}

/** Reads an F7 item body (get, update, create, disable, from-unit with money_contract=2). */
export function readPricingProfileResponseV2(input: unknown): ManagementRead<PricingProfileManagementV2> {
  return body(input, (record) => {
    const raw = field(record, "pricing_profile", "");
    const read = profiles(record, [raw], () => "pricing_profile");
    return { profile: read.profiles[0], operating_currency: read.operating_currency, currency_choices: read.currency_choices };
  });
}

/** Reads an F7 list body (GET /api/org/<id>/pricing-profiles?money_contract=2), profiles in server order. */
export function readPricingProfileListResponseV2(input: unknown): ManagementRead<PricingProfileListManagementV2> {
  return body(input, (record) => profiles(record, array(field(record, "pricing_profiles", ""), "pricing_profiles"), (i) => `pricing_profiles[${i}]`));
}

function appliedProfile(value: unknown, path: string): AppliedPricingProfileV2 {
  const record = object(value, path);
  const int = (key: string) => integer(field(record, key, path), join(path, key), "profile_field");
  const str = (key: string) => text(field(record, key, path), join(path, key), "profile_field");
  const optionalText = (key: string) => {
    const v = field(record, key, path);
    return v === null ? null : text(v, join(path, key), "profile_field");
  };
  const tariffPath = join(path, "money_v2");
  const money_v2 = tariff(field(record, "money_v2", path), tariffPath);
  if (money_v2.tariff_state === "unset") throw new Invalid("state", join(tariffPath, "tariff_state"), "an applied profile always stores a pricebook");
  return {
    assignment_id: int("assignment_id"),
    profile_id: int("profile_id"),
    profile_name: str("profile_name"),
    audience_type: str("audience_type"),
    audience_id: int("audience_id"),
    audience_name: str("audience_name"),
    resource_type: str("resource_type"),
    scope: str("scope"),
    scope_type: str("scope_type"),
    status: str("status"),
    valid_from_utc: optionalText("valid_from_utc"),
    valid_until_utc: optionalText("valid_until_utc"),
    precedence: int("precedence"),
    money_v2,
  };
}

/**
 * Reads an F14 body (GET /api/units/<id>/pricing-context?money_contract=2): the marker,
 * the unit's persisted tariff in money_v2.tariff, each applied profile's tariff in its own
 * money_v2, in server order, and the response-level legacy-key rule. default_pricebook and
 * pricebook are never read as money and may be absent; a body with tenant members is not
 * an F14 body.
 */
export function readPricingContextResponseV2(input: unknown): ManagementRead<PricingContextV2> {
  return body(input, (record) => {
    const moneyV2 = object(field(record, "money_v2", ""), "money_v2");
    const tenant = TENANT_MEMBERS.find((key) => has(moneyV2, key));
    if (tenant !== undefined) throw new Invalid("tenant_members_present", join("money_v2", tenant), "a pricing context carries no tenant currency or choices");
    const stored = tariff(field(moneyV2, "tariff", "money_v2"), "money_v2.tariff");
    const raw = array(field(record, "applied_profiles", ""), "applied_profiles");
    const pathOf = (i: number) => `applied_profiles[${i}]`;
    const applied_profiles = raw.map((p, i) => appliedProfile(p, pathOf(i)));
    // Legacy keys are response-level: one ineligible tariff makes the legacy answer a whole 409.
    if (![stored, ...applied_profiles.map((p) => p.money_v2)].every(eligibleLegacy)) {
      noLegacyKeys(record, CONTEXT_LEGACY_KEYS, "");
      raw.forEach((p, i) => noLegacyKeys(p as Record<string, unknown>, PROFILE_LEGACY_KEYS, pathOf(i)));
    }
    const organizationName = record.organization_name ?? null;
    return {
      unit_id: text(field(record, "unit_id", ""), "unit_id", "context_field"),
      organization_id: integer(field(record, "organization_id", ""), "organization_id", "context_field"),
      organization_name: organizationName === null ? null : text(organizationName, "organization_name", "context_field"),
      applied_profiles,
      precedence_explanation: text(field(record, "precedence_explanation", ""), "precedence_explanation", "context_field"),
      money_v2: { tariff: stored },
    };
  });
}

/**
 * Whether an exact rate amount is within the new-input limits of money
 * contract 2 (at most 9 integer and 6 fraction digits). A stored legacy rate
 * outside them keeps every digit for display but cannot be written back by a
 * new operator write; the server enforces the same limit.
 */
export function isNewInputRateAmount(amount: string): boolean {
  return readRateV2({ currency: "USD", amount, unit: "kWh", precision_origin: "new_input", money_version: 2 }).status === "value";
}
